import React, { createRef, Fragment, PureComponent } from 'react'
import { Formik } from 'formik'
import Tooltip from '@mui/material/Tooltip'
import DurationInput from '../shared/durationInput'
import { Attachment, MenuCategory, OrderProvider } from './types'
import OutlinedInput from '@mui/material/OutlinedInput'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import Select from '@mui/material/Select'
import Checkbox from '@mui/material/Checkbox'
import map from 'lodash/map'
import find from 'lodash/find'
import capitalize from 'lodash/capitalize'
import { LocalizationContext } from '../shared/contexts'
import { t } from '../../i18n'
import PORTION_TYPES from '../shared/recipePortionTypes'

const recipeTypeOptions = map(PORTION_TYPES, type => (
  { label: t(`recipes.${type}`), value: type }
))

interface Props {
  id?: number,
  ref: string,
  name: string,
  price: string,
  preparation_time: string,
  portion_type: string,
  clean_at: string,
  processing_loss: string,
  attachment: Attachment,
  simphony_external_id?: string,
  infor_external_id?: string,
  forecasted_order_quantity: number,
  forecasted_order_quantity_enabled: boolean,
  removeAttachment: Function,
  imageWithinForm: string,
  imageUrl: string,
  handleChange: Function,
  fixErrorState: Function,
  errorMessages: {},
  menu_category: MenuCategory,
  menuCategoriesOptions: MenuCategory[],
  orderProviders: OrderProvider[],
  readOnly: boolean
}

class RecipeEditForm extends PureComponent<Props> {

  formikRef: any
  imageWithinFormRef: any

  constructor(props: Props) {
    super(props)

    this.formikRef = createRef()
    this.imageWithinFormRef = createRef()
  }

  renderErrors = (field) => {
    return (
      <Fragment>
        {map(this.props.errorMessages[field], (errorMessage, i) =>
          <span className="error-text" style={{marginTop: `${i * 15}px`}}>{errorMessage}</span>
        )}
      </Fragment>
    )
  }

  handleInputChange = (name, value) => {
    this.props.handleChange(name, value)
    this.props.fixErrorState(name)
  }

  renderInfoIcon = (text: string) => (
    <Tooltip title={text} placement='top' arrow>
      <i className='bi bi-info-circle recipe-field-info' aria-label={text} />
    </Tooltip>
  )

  renderInput = (name, type, label, value, handleBlur, customBlur = false, info?: string) => {
    return (
      <div className="recipe-input-wrapper">
        {label && <label>{label}{info && this.renderInfoIcon(info)}</label>}
        <input
          id={name}
          type={type}
          name={name}
          onBlur={({ target: { value } }) => { if (customBlur && !this.props.readOnly) handleBlur(value) }}
          value={value}
          checked={value}
          readOnly={this.props.readOnly}
          onChange={({ target: { name, value } }) => {this.handleInputChange(name, value)}}
          className={`form-control inverted ${this.props.errorMessages[name] ? 'error-state' : ''}`}
          placeholder={label && `Enter ${label}`}
        />
        {this.props.errorMessages[name] && this.renderErrors(name) }
      </div>
    )
  }

  renderCategorySelectValue = (selectedCategory) => {
    if (!selectedCategory) return t('categories')

    const category = find(this.props.menuCategoriesOptions, o => o.value === selectedCategory)
    return category ? category.label : t('categories')
  }

  renderSelect = (field, selectOptions, handleChange, value) => {
    if (!selectOptions) return null

    const findMenuCategoryById = (id) => {
      const category = find(selectOptions, (option: any) => option.value === id)
      return category ? {id: category.value, label: category.label} : null
    }

    return (
      <Select
        className='recipe-select'
        value={value || ''}
        disabled={this.props.readOnly}
        onChange={(opt) => handleChange(field, findMenuCategoryById(opt.target?.value))}
        displayEmpty={true}
        input={<OutlinedInput />}
        renderValue={this.renderCategorySelectValue}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 224,
              width: 250,
            },
          }
        }}
        sx={{ color: !!value ? 'black' : '#757575' }}
      >
        {selectOptions.map((option: any) => (
          <MenuItem key={option.value} value={option.value}>
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    )
  }

  render = () => (
    <Formik
      innerRef={this.formikRef}
      enableReinitialize
      onSubmit={() => {}}
      initialValues={{
        id: this.props.id,
        name: this.props.name,
        price: this.props.price,
        preparation_time: this.props.preparation_time,
        attachment: this.props.attachment,
        imageWithinForm: this.props.imageWithinForm,
        imageUrl: this.props.imageUrl,
        menu_category: this.props.menu_category,
        portion_type: this.props.portion_type,
        clean_at: this.props.clean_at,
        processing_loss: this.props.processing_loss,
        simphony_external_id: this.props.simphony_external_id,
        infor_external_id: this.props.infor_external_id,
      }}
    >
      {({
        values,
        errors,
        touched,
        status,
        handleBlur,
        handleSubmit,
        setStatus,
        setFieldValue,
      }) => {
        const handleImageChange = ({ target: { files } }) => {
          if (!files || !files[0]) return

          const { handleChange } = this.props

          handleChange('imageWithinForm', files[0].name)
          handleChange('imageUrl', URL.createObjectURL(files[0]))
        }

        const renderOrderProviderFields = () => {
          const { orderProviders } = this.props
          if (!orderProviders || orderProviders.length === 0) return null

          return (
            <div className='recipe-order-provider-field'>
             {orderProviders.map((provider) => {
              const fieldName = `${provider.provider_type?.toLowerCase()}_external_id`
              const fieldLabel = `${capitalize(provider.provider_type)} External ID`
              return (
                <div className="form-group" key={fieldName}>
                  {this.renderInput(fieldName, 'text', fieldLabel, this.props[fieldName], () => {})}
                </div>
              )
             })}
            </div>
          )
        }

        const renderFileUpload = () => {
          const { attachment, readOnly } = this.props
          const imageUrl  = values.imageUrl || (attachment && attachment.file_url) || '/images/missing_image.svg'
          const imagePreview = (
            <img src={imageUrl} className={`image-preview ${this.props.errorMessages['attachment'] ? 'error-state' : ''}`}/>
          )

          if (readOnly) {
            return (
              <div className='custom-file'>
                {imagePreview}
              </div>
            )
          }

          return (
            <div className='custom-file'>
              <input
                style={{ display: 'none' }}
                id='imageWithinForm'
                type='file'
                accept='image/jpeg,image/png'
                ref={this.imageWithinFormRef}
                name='imageWithinForm'
                onChange={handleImageChange}
                aria-describedby='imageWithinForm'
              />
              <Tooltip title='Change image' placement='top' className='task-parameter' arrow>
                <label htmlFor='imageWithinForm' className='pointer'>
                  {imagePreview}
                </label>
              </Tooltip>
            </div>
          )
        }

        const renderRecipeTypeOptions = () => {
          return (
            <div className='form-group mt-3'>
              <label>{t('recipes.type')}</label>
              <div className='mt-1'>
                {recipeTypeOptions.map((option, index) => (
                  <div
                    onClick={() => { if (!this.props.readOnly) handleTypeChange(option.value) }}
                    key={option.value}
                    className={`d-inline ${index === 0 ? 'ms-3' : 'ms-3'}`}
                  >
                    <input
                      onChange={() => { handleTypeChange(option.value) }}
                      type='radio'
                      name={option.value}
                      disabled={this.props.readOnly}
                      checked={this.props.portion_type === option.value}
                    />
                    <label
                      htmlFor={option.value}
                      className='pointer ms-1'
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )
        }

        const handlePriceChange = (price) => {
            this.props.handleChange('price', Number(price).toFixed(2))
        }

        const handleTypeChange = (portionType) => {
          this.props.handleChange('portion_type', portionType)
        }

        return (
          <form className={`recipe-form ${this.props.readOnly ? 'read-only' : ''}`} autoComplete='off'>
            <div className='recipe-header'>
              <div className='form-group recipe-image'>
                {renderFileUpload()}
              </div>
              <div className='form-group recipe-name'>
                {this.renderInput('name', 'text', 'Name', this.props.name, handleBlur)}
                {/* {renderRecipeTypeOptions()} */} {/* temporarily disable until robot has batch recipes */}
              </div>
              <div className="form-group recipe-group" style={{marginTop: '3px'}}>
                <label>Menu Category</label>
                {this.renderSelect('menu_category', this.props.menuCategoriesOptions, this.props.handleChange, values['menu_category']?.id)}
              </div>
              <div className='form-group recipe-price'>
                <LocalizationContext.Consumer>
                  {({ currency }) =>
                    this.renderInput('price', 'number', `Price (${currency?.code})`, this.props.price, handlePriceChange, true)
                  }
                </LocalizationContext.Consumer>
                {this.props.portion_type == 'batch' &&
                  <div className="mt-3">
                    {this.renderInput('clean_at', 'number', 'Clean feeder at (g)', this.props.clean_at, handleBlur)}
                  </div>
                }
              </div>
              <div className='form-group recipe-preparation-time recipe-input-wrapper'>
                <label>Preparation Time</label>
                <DurationInput
                  className={`form-control inverted ${this.props.errorMessages['preparation_time'] ? 'error-state' : ''}`}
                  value={values.preparation_time}
                  disabled={this.props.readOnly}
                  onChange={(value) => this.props.handleChange('preparation_time', value)}
                />
                  {this.props.errorMessages['preparation_time'] && this.renderErrors('preparation_time') }
              </div>
            </div>
            <div className="recipe-header no-image">
              {renderOrderProviderFields()}
              <div className="form-group recipe-group">
                <div className="recipe-processing-loss">
                  {this.renderInput('processing_loss', 'number', 'Processing loss (%)', this.props.processing_loss, handleBlur, false, t('recipes.processing_loss_info'))}
                </div>
              </div>
              {/* Temporarily disabled until auto-replenishment is used */}
              {/* <div className="form-group">
                <div className="recipe-input-wrapper">
                  <label>
                    <input
                      type="checkbox"
                      name="forecasted_order_quantity_enabled"
                      checked={this.props.forecasted_order_quantity_enabled}
                      onChange={({ target: { name, checked } }) => {this.handleInputChange(name, checked)}}
                      className="me-2"
                    />
                    Enable Forecasted Order Quantity
                  </label>
                </div>
                {this.props.forecasted_order_quantity_enabled && (
                  <div className="mt-2">
                    {this.renderInput('forecasted_order_quantity', 'number', '', this.props.forecasted_order_quantity || '', handleBlur)}
                  </div>
                )}
              </div> */}
            </div>
          </form>
        )
      }}
    </Formik>
  )
}

export default RecipeEditForm
